import type { Attachment, AttachmentList } from "@esi/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Info, Plus, RotateCcw, Trash2 } from "lucide-react";
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useTranslation } from "react-i18next";
import { treeRowElement } from "@/components/tree/Tree";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { failureText } from "../../projects/FileRow.tsx";
import { sectionId, type TreeNode } from "../../projects/knowledge-tree.ts";
import { useSession } from "../../session.tsx";
import {
  AttachmentDelete,
  AttachmentDetailsAnchored,
  AttachmentDetailsBody,
  AttachmentDownload,
  AttachmentOpen,
  AttachmentStatus,
  attachmentRowActions,
} from "./AttachmentControls.tsx";
import { attachmentApi } from "./api.ts";
import { accessDenied, attachmentActionError, canRetryAttachment } from "./controller.ts";
import { mediaIcon } from "./media.ts";
import { clearAttachmentAccess, invalidateAttachments, useConfiguredDraft } from "./queries.ts";

export { attachmentRowActions };

/**
 * Where focus goes once a deleted attachment's row leaves the tree: the next attachment row,
 * else the previous one, else the This conversation section row, else the next remaining row
 * (an empty section is not shown), else `fallback` (the sheet); never the document body.
 * `returnFocus` serves the delete dialog when it closes after the row is gone; the effect, a row
 * that leaves after the dialog handed focus back to it.
 */
export function useAttachmentDeleteFocus(
  rows: readonly { id: string; node: TreeNode }[],
  container: RefObject<HTMLElement | null>,
  setFocused: (id: string) => void,
  fallback: () => HTMLElement | null | undefined = () => null,
) {
  const pending = useRef<{
    id: string;
    candidates: string[];
    /** The dialog the delete was confirmed in, still closing when the row goes. */
    dialog: Element | null;
  } | null>(null);
  const take = (present: (id: string) => boolean) => {
    const target = pending.current?.candidates.find(present);
    pending.current = null;
    if (target) {
      setFocused(target);
    }
    return target;
  };
  useEffect(() => {
    const p = pending.current;
    if (!p || rows.some((r) => r.id === p.id)) {
      return;
    }
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) {
      // Still inside the closing dialog: its return-focus takes the neighbour instead. Focus
      // the user moved elsewhere is theirs: the target is dropped, never applied later.
      if (!p.dialog?.contains(active)) {
        pending.current = null;
      }
      return;
    }
    const target = take((id) => rows.some((r) => r.id === id));
    setTimeout(() => (target ? treeRowElement(container.current, target) : fallback())?.focus(), 0);
  });
  const isAttachment = (row: { node: TreeNode } | undefined) =>
    row?.node.kind === "item" && row.node.type === "attachment";
  return {
    deleted: (rowId: string) => {
      const index = rows.findIndex((r) => r.id === rowId);
      const after: string[] = [];
      for (let i = index + 1; isAttachment(rows[i]); i++) {
        after.push(rows[i]?.id ?? "");
      }
      const before: string[] = [];
      for (let i = index - 1; i >= 0 && isAttachment(rows[i]); i--) {
        before.push(rows[i]?.id ?? "");
      }
      const rest = [rows[index + after.length + 1]?.id, rows[0]?.id].filter(
        (id): id is string => id !== undefined && id !== rowId,
      );
      pending.current = {
        id: rowId,
        candidates: [...after, ...before, sectionId("attachments"), ...rest],
        dialog: document.activeElement?.closest('[role="dialog"], [role="alertdialog"]') ?? null,
      };
    },
    returnFocus: (rowId: string) => {
      const row = treeRowElement(container.current, rowId);
      if (row?.isConnected || !pending.current) {
        return row;
      }
      const target = take((id) => treeRowElement(container.current, id)?.isConnected === true);
      return target ? treeRowElement(container.current, target) : (fallback() ?? null);
    },
  };
}

/** The same compact working-file row in the project tree and standalone files sheet. */
export function AttachmentRow(props: {
  conversationId: string;
  attachment: Attachment;
  list: AttachmentList | undefined;
  style?: CSSProperties;
  name: ReactNode;
  nameId: string;
  detailsId: string;
  menu: (items: ReactNode) => ReactNode;
  returnFocus: () => HTMLElement | null;
  /** The delete went through; the tree moves focus once the row is gone. */
  onDeleted?: () => void;
  /** Where the delete dialog returns focus; the row itself when absent. */
  deleteReturnFocus?: () => HTMLElement | null;
}) {
  const { attachment: a, list, conversationId } = props;
  const { t } = useTranslation();
  const draft = useConfiguredDraft(conversationId);
  // This row's selection only: typing in the composer never re-renders the rows.
  const selected = useSyncExternalStore(draft.subscribe, () =>
    draft.snapshot().selected.includes(a.id),
  );
  const { csrfToken } = useSession();
  const client = useQueryClient();
  const actions = attachmentRowActions(a, list);
  const [deleting, setDeleting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [details, setDetails] = useState(false);
  const [error, setError] = useState<string>();
  const Icon = mediaIcon(a.mediaType);
  // Failed and paused rows keep why inline; everything else waits in the details.
  const reason =
    a.status === "failed" && a.failureReason && a.failureReason !== "spend_limit"
      ? failureText(a.failureReason, null)
      : null;
  const retry = () => {
    setRetrying(true);
    setError(undefined);
    void attachmentApi(conversationId, csrfToken)
      .retry(a.id)
      .then(() => invalidateAttachments(client, conversationId))
      .catch((e) => {
        if (accessDenied(e)) {
          clearAttachmentAccess(client, conversationId);
        }
        invalidateAttachments(client, conversationId);
        setError(attachmentActionError(e));
      })
      .finally(() => setRetrying(false));
  };
  // Let the menu or popover release its focus trap before the next overlay opens.
  const later = (open: () => void) => setTimeout(open, 0);
  return (
    <AttachmentDetailsAnchored
      open={details}
      onOpenChange={setDetails}
      title={a.title}
      returnFocus={props.returnFocus}
      anchor={
        <span
          data-esi-content="files"
          className="flex min-w-0 items-start gap-1 py-2 pr-1.5 text-sm"
          style={props.style}
        >
          <Icon
            aria-hidden="true"
            className="mt-0.5 mr-1.5 size-4 shrink-0 text-fg-muted"
            strokeWidth={1.5}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span id={props.nameId} title={a.title} className="truncate font-medium text-fg">
              {props.name}
            </span>
            <span id={props.detailsId} className="flex min-w-0 flex-col gap-0.5">
              <span className="flex min-w-0 items-center gap-1.5">
                <AttachmentStatus attachment={a} />
                {selected && actions.add ? (
                  <span className="truncate text-xs text-fg-secondary">
                    · {t("attachments.selected")}
                  </span>
                ) : null}
              </span>
              {reason ? (
                <span className="text-xs text-fg-secondary">
                  {reason}
                  {!canRetryAttachment(a) ? ` ${t("attachments.reselect")}` : ""}
                </span>
              ) : null}
              {error ? (
                <span role="alert" className="text-xs text-danger-fg">
                  {error}
                </span>
              ) : null}
            </span>
          </span>
          {props.menu(
            <>
              <DropdownMenuItem onSelect={() => later(() => setDetails(true))}>
                <Info aria-hidden="true" />
                {t("attachments.details")}
              </DropdownMenuItem>
              {actions.add && !selected ? (
                <DropdownMenuItem onSelect={() => draft.select(a.id, true)}>
                  <Plus aria-hidden="true" />
                  {t("attachments.addToMessage")}
                </DropdownMenuItem>
              ) : null}
              <AttachmentOpen
                conversationId={conversationId}
                attachment={a}
                menu
                returnFocus={props.returnFocus}
              >
                {t("files.open")}
              </AttachmentOpen>
              <AttachmentDownload conversationId={conversationId} attachment={a} menu>
                {t("artefacts.panel.download")}
              </AttachmentDownload>
              {actions.retry ? (
                <DropdownMenuItem disabled={retrying} onSelect={retry}>
                  <RotateCcw aria-hidden="true" />
                  {t("attachments.retry")}
                </DropdownMenuItem>
              ) : null}
              {actions.delete ? (
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => later(() => setDeleting(true))}
                >
                  <Trash2 aria-hidden="true" />
                  {t("attachments.delete")}
                </DropdownMenuItem>
              ) : null}
            </>,
          )}
          <AttachmentDelete
            conversationId={conversationId}
            attachment={a}
            canManage={actions.delete}
            hideTrigger
            open={deleting}
            onOpenChange={setDeleting}
            returnFocus={props.deleteReturnFocus ?? props.returnFocus}
            onDeleted={() => {
              // Removing the local entry deselects it too.
              const local = draft.snapshot().files.find((f) => f.attachmentId === a.id);
              if (local) {
                draft.remove(local.key);
              } else {
                draft.select(a.id, false);
              }
              props.onDeleted?.();
            }}
          />
        </span>
      }
    >
      <AttachmentDetailsBody
        title={a.title}
        attachment={a}
        list={list}
        mediaType={a.mediaType}
        actions={
          <>
            {actions.add && !selected ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => {
                  draft.select(a.id, true);
                  setDetails(false);
                }}
              >
                <Plus aria-hidden="true" />
                {t("attachments.addToMessage")}
              </Button>
            ) : null}
            <AttachmentOpen conversationId={conversationId} attachment={a}>
              {t("files.open")}
            </AttachmentOpen>
            <AttachmentDownload conversationId={conversationId} attachment={a}>
              {t("artefacts.panel.download")}
            </AttachmentDownload>
            {actions.retry ? (
              <Button type="button" variant="ghost" size="xs" disabled={retrying} onClick={retry}>
                <RotateCcw aria-hidden="true" />
                {t("attachments.retry")}
              </Button>
            ) : null}
            {actions.delete ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="text-danger-fg"
                onClick={() => {
                  setDetails(false);
                  later(() => setDeleting(true));
                }}
              >
                <Trash2 aria-hidden="true" />
                {t("attachments.delete")}
              </Button>
            ) : null}
          </>
        }
      />
    </AttachmentDetailsAnchored>
  );
}
